import {testIdProps} from '@github-ui/test-id-props'
import {ClockIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {memo, type RefObject, startTransition, useCallback, useRef, useState} from 'react'

import {MergeabilitySidesheet} from './MergeabilitySidesheet'
import {presentationForStatus, Status, mergeabilityStatus} from '../helpers/mergeability-status'
import {useMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {useMergeabilityLiveUpdates, type Channels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './MergeStatusButton.module.css'
import {clsx} from 'clsx'

const MergeStatusButtonFallback = memo(function MergeStatusButtonFallback({
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
}: {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}) {
  return (
    <Button
      ref={mergeStatusButtonRef}
      size="small"
      leadingVisual={ClockIcon}
      onClick={() => toggleMergeabilitySidesheet(true)}
    >
      Calculating mergeability…
    </Button>
  )
})

interface MergeStatusButtonCommonProps {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}

/**
 * A button that displays the merge status of a pull request.
 * Opens the mergeability sidesheet.
 */
export const MergeStatusButtonWrapper = memo(function MergeStatusButtonWrapper({
  channels,
  headSha,
  helpUrl,
  viewerLogin,
}: {
  channels: Channels
  headSha: string
  helpUrl: string
  viewerLogin: string
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useMergeBoxPageData({mergeMethod, bypassRequirements: false})
  const {data: statusChecksData} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const [mergeabilitySidesheetIsOpen, setMergeabilitySidesheetIsOpen] = useState(false)
  const toggleMergeabilitySidesheet = useCallback((isOpen: boolean) => {
    setMergeabilitySidesheetIsOpen(isOpen)
  }, [])

  const sidesheet = (
    <MergeabilitySidesheet
      helpUrl={helpUrl}
      mergeStatusButtonRef={mergeStatusButtonRef}
      mergeabilitySidesheetIsOpen={mergeabilitySidesheetIsOpen}
      toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
      viewerLogin={viewerLogin}
      channels={channels}
    />
  )

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels})

  // Show fallback while data is loading
  if (!data || !statusChecksData)
    return (
      <>
        <MergeStatusButtonFallback
          mergeStatusButtonRef={mergeStatusButtonRef}
          toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
        />
        {sidesheet}
      </>
    )

  return (
    <>
      <MergeStatusButton
        pullRequest={data.pullRequest}
        mergeRequirements={data.mergeRequirements}
        statusChecksData={statusChecksData}
        mergeStatusButtonRef={mergeStatusButtonRef}
        toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
      />
      {sidesheet}
    </>
  )
})

export type MergeStatusButtonData = MergeBoxPageData

export function MergeStatusButton({
  pullRequest,
  mergeRequirements,
  statusChecksData,
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
}: MergeStatusButtonData & MergeStatusButtonCommonProps & {statusChecksData: StatusChecksPageData}) {
  if (!mergeStatusButtonRef) return null

  const status = mergeabilityStatus({pullRequest, mergeRequirements, statusChecksData})
  // Don't render merge status button if failure is non-actionable (repo is not writeable, etc.)
  if (status === Status.NonactionableFailure) return null

  const presentation = presentationForStatus(status)
  const isPrimary = status === Status.Mergeable && pullRequest.viewerDidAuthor

  return (
    <Button
      ref={mergeStatusButtonRef}
      leadingVisual={presentation.icon}
      variant={isPrimary ? 'primary' : 'default'}
      {...testIdProps('merge-status-button')}
      className={clsx(isPrimary ? styles.successIsPrimary : styles[presentation.iconColorClass])}
      onClick={() => {
        toggleMergeabilitySidesheet?.(true)
      }}
      size="small"
    >
      <span className="d-flex hide-lg hide-xl">View status</span>
      <span className="hide-sm hide-md d-lg-flex gap-1">{presentation.title}</span>
    </Button>
  )
}
